import React, { useState, useRef, useEffect } from "react";
import useApp from "../../../store/contexts/AppContext";
import {
  MESSAGE_FILE_TYPES,
  MESSAGE_INTERACTIONS_TYPES,
  CHAT_ROOMS,
} from "../../../config/index";
import TruncateString from "../../../utils/TruncateString";
import FormatDateDurationForMessagesTime from "../../../utils/FormatDateDurationForMessagesTime";
import FormatDateDurationForMessagesDate from "../../../utils/FormatDateDurationForMessagesDate";
import capitalizeEachFirstWord from "../../../utils/CapLetters";
import FormatNumberCounter from "../../../utils/FormatNumberCounter";
import { getParticipantData, getTeamCode } from "../../../utils/RoomHelpers";
import MenuIcon from "@mui/icons-material/Menu";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import EditIcon from "@mui/icons-material/Edit";
import ReplyIcon from "@mui/icons-material/Reply";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import DeleteIcon from "@mui/icons-material/Delete";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CancelIcon from "@mui/icons-material/Cancel";
import "./style.scss";

import { makeStyles } from "@mui/styles";
import { ActiveChatInRoomStyle } from "./ActiveChatInRoomStyle";

const useStyles = makeStyles((theme) => ActiveChatInRoomStyle(theme));

const MessageField = ({ otherUser, innerRoom, roomModerators }) => {
  const classes = useStyles();

  const {
    appState: { activeChat, userData },
    sendMessageToChat,
    deleteChatMessageApiCall,
    editChatMessageApiCall,
    updateChatMessageInteractionApiCall,
    updateChatMessageTipApiCall,
  } = useApp();

  const [message, setMessage] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [replyMessage, setReplyMessage] = useState(null);
  const [selectedRepliedMessageId, setSelectedRepliedMessageId] =
    useState(null);
  const [editedMessage, setEditedMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditableMessage, setIsEditableMessage] = useState(false);
  const [shouldShowOlderMessagesNotice, setShouldShowOlderMessagesNotice] =
    useState(false);
  const [displayMessages, setDisplayMessages] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [activeActionMessageId, setActiveActionMessageId] = useState(null);

  const messagesEndRef = useRef(null);
  const selectedRepliedMessageIdRef = useRef(null);
  const actionTimeoutRef = useRef(null);

  const receiverId = otherUser.id;
  const newChat = activeChat.has_connected_before;
  const roomId = activeChat.id;
  const roomTeams = activeChat.room_teams;

  const isCurrentUserModerator = roomModerators.some(
    (moderator) => moderator.id?.toString() === userData?.id?.toString()
  );

  // Effect to manage displayed messages
  useEffect(() => {
    if (activeChat.type === CHAT_ROOMS.TYPES.GROUP) {
      const limitedMessages = getLastMessages(activeChat.messages);
      setDisplayMessages(limitedMessages);
      setShouldShowOlderMessagesNotice(activeChat.messages.length > 100);
    } else {
      setDisplayMessages(activeChat.messages);
      setShouldShowOlderMessagesNotice(false);
    }
  }, [activeChat.messages, activeChat.type]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    return () => {
      if (actionTimeoutRef.current) {
        clearTimeout(actionTimeoutRef.current);
      }
    };
  }, []);

  const getLastMessages = (messages, limit = 100) => {
    if (messages.length <= limit) return messages;
    return messages.slice(Math.max(messages.length - limit, 0));
  };

  const getMessageLineEstimate = (text = "", charsPerLine = 42) => {
    const lines = text.split("\n").reduce((count, line) => {
      const estimatedLineLength = Math.max(1, Math.ceil(line.length / charsPerLine));
      return count + estimatedLineLength;
    }, 0);

    return Math.max(lines, 1);
  };

  const getMessageSizeClass = (text = "") => {
    const lines = getMessageLineEstimate(text);

    if (lines <= 1) return "oneLine";
    if (lines === 2) return "twoLines";
    return "threeLines";
  };

  const toggleExpandedMessage = (messageId) => {
    setExpandedMessages((prevState) => ({
      ...prevState,
      [messageId]: !prevState[messageId],
    }));
  };

  const clearActiveActionTimeout = () => {
    if (actionTimeoutRef.current) {
      clearTimeout(actionTimeoutRef.current);
      actionTimeoutRef.current = null;
    }
  };

  const closeActiveMessageActions = () => {
    clearActiveActionTimeout();
    setActiveActionMessageId(null);
  };

  const openActiveMessageActions = (messageId) => {
    clearActiveActionTimeout();
    setActiveActionMessageId(messageId);
    actionTimeoutRef.current = setTimeout(() => {
      setActiveActionMessageId((currentId) =>
        currentId?.toString() === messageId?.toString() ? null : currentId
      );
    }, 5000);
  };

  const handleToggleMessageActions = (messageId) => {
    if (activeActionMessageId?.toString() === messageId?.toString()) {
      closeActiveMessageActions();
      return;
    }

    openActiveMessageActions(messageId);
  };

  const handleMenuOptionClick = (event, currentSelectedMessage) => {
    setSelectedMessage(currentSelectedMessage);
    const editable = isMessageEditable(currentSelectedMessage);
    setIsEditableMessage(editable);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuOptionClose = () => {
    setSelectedMessage(null);
    setAnchorEl(null);
    setIsEditableMessage(false);
    closeActiveMessageActions();
  };

  const handleSendMessage = () => {
    if (message !== "") {
      const messageDataToSend = {
        roomId,
        message,
        receiverId,
        fileType: MESSAGE_FILE_TYPES.TEXT,
        roomIsNew: !newChat,
        replyToId: replyMessage ? replyMessage.id : null,
      };
      sendMessageToChat(messageDataToSend);

      if (replyMessage) {
        setReplyMessage(null);
      }
      setMessage("");
      !isCurrentUserModerator &&
        setCountdown(
          Number(userData.available_xp) >= activeChat.user_xp_level ? 15 : 30
        );
    }
  };

  const handleSendOrEdit = () => {
    if (editedMessage) {
      const messageId = selectedMessage.id;
      const dataToUpdate = { message: editedMessage };
      editChatMessageApiCall(roomId, messageId, dataToUpdate);
      setEditedMessage("");
    } else {
      if (isCurrentUserModerator || countdown === 0) {
        handleSendMessage();
      }
    }
  };

  const handleKeyDown = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendOrEdit();
    }
  };

  const getRepliedMsg = (messageId) => {
    if (!messageId) return null;
    const result = activeChat.messages.find(
      (message) => message.id.toString() === messageId?.toString()
    );
    return result ? { id: result?.id, message: result?.message } : null;
  };

  const cancelMessageEditing = () => {
    if (editedMessage) {
      setEditedMessage("");
    }
  };

  const handleEditMessage = async () => {
    setAnchorEl(null);
    if (!selectedMessage && !isEditableMessage) return;
    setEditedMessage(selectedMessage.message);
    setIsEditableMessage(false);
    closeActiveMessageActions();
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;
    const messageId = selectedMessage.id;
    await deleteChatMessageApiCall(roomId, messageId);
    closeActiveMessageActions();
  };

  const EDIT_WINDOW_MS = 15 * 60 * 1000;
  const isMessageEditable = (msg) => {
    if (!msg) return false;
    if (msg.is_editable_until) return Date.now() < Date.parse(msg.is_editable_until);
    if (msg.local_sent_at) return (Date.now() - Number(msg.local_sent_at)) < EDIT_WINDOW_MS;
    if (msg.created_at) return (Date.now() - Date.parse(msg.created_at + (msg.created_at.includes('Z') || msg.created_at.includes('+') ? '' : 'Z'))) < EDIT_WINDOW_MS;
    return false;
  };

  const handleReplyMessage = (message) => {
    const truncatedMessage = TruncateString(message.message, 100);
    setReplyMessage({ id: message.id, message: truncatedMessage });
    closeActiveMessageActions();
  };

  const handleScrollToRepliedMessage = (messageId) => {
    setSelectedRepliedMessageId(messageId);
  };

  const handleMsgInteraction = (type, messageId) => {
    updateChatMessageInteractionApiCall(roomId, messageId, type);
    closeActiveMessageActions();
  };

  const handleMsgTipping = (messageId) => {
    updateChatMessageTipApiCall(roomId, messageId);
    closeActiveMessageActions();
  };

  useEffect(() => {
    if (selectedRepliedMessageId) {
      selectedRepliedMessageIdRef.current?.scrollIntoView({
        behavior: "smooth",
      });
      const timeoutId = setTimeout(() => {
        setSelectedRepliedMessageId(null);
      }, 10000);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedRepliedMessageId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeChat.messages]);

  let previousDate = null;

  return (
    <div>
      <div className={classes.balanceContainer}>
        <div
          className={classes.balanceInnerLeft}
          title={userData?.available_coins?.toLocaleString()}
        >
          <b>Coins:</b>{" "}
          <span className={classes.balanceInnerLeftValue}>
            {FormatNumberCounter(userData?.available_coins || 0)}
          </span>
        </div>
        <div
          className={classes.balanceInnerRight}
          title={userData?.available_xp?.toLocaleString()}
        >
          <b>XP:</b>{" "}
          <span className={classes.balanceInnerRightValue}>
            {FormatNumberCounter(userData?.available_xp || 0)}
          </span>
        </div>
      </div>
      <div className="chatContainer2">
        <div className="chatMessages2">
          {shouldShowOlderMessagesNotice && (
            <div className="chatMessagesTopNotice">
              Only showing the last 100 messages
            </div>
          )}

          {!displayMessages?.length && (
            <div style={{ textAlign: "center" }}>
              <h2>Send messages across</h2>
            </div>
          )}

          {displayMessages.map((message) => {
            const messageSenderData = getParticipantData(
              activeChat.room_participants,
              message.sender_id
            );
            const messageSenderTeamId = messageSenderData?.room_team_id;
            const messageInteraction = message.interaction;
            const truncatedUsername = TruncateString(
              messageSenderData?.username,
              20
            );

            const getRepliedMessage = getRepliedMsg(message.reply_to_id);
            const truncatedRepliedMessage = getRepliedMessage
              ? TruncateString(getRepliedMessage.message, 100)
              : null;
            const formattedMessageDate = FormatDateDurationForMessagesDate(
              message.created_at
            );
            const formattedMessageTime = FormatDateDurationForMessagesTime(
              message.created_at
            );
            const messageOwner =
              userData.id?.toString() === message.sender_id?.toString()
                ? "self"
                : "other";

            const userTeamCode =
              messageSenderData.is_admin === 1
                ? "admin"
                : getTeamCode(roomTeams, messageSenderTeamId);

            const isMyMessage =
              userData.id?.toString() === message.sender_id?.toString();

            const likeInteractionCount = messageInteraction?.count?.like;
            const dislikeInteractionCount = messageInteraction?.count?.dislike;

            const myMsgInteractionType = messageInteraction?.type ?? null;
            const usernameLabel =
              messageOwner === "self"
                ? "You"
                : capitalizeEachFirstWord(truncatedUsername);
            const avatarFallback = usernameLabel?.charAt(0)?.toUpperCase() || "?";
            const avatarSrc =
              messageSenderData?.profile_image_url ||
              messageSenderData?.profile_picture ||
              undefined;
            const estimatedMessageLines = getMessageLineEstimate(message.message);
            const isExpandableMessage = estimatedMessageLines > 3;
            const isExpandedMessage = Boolean(expandedMessages[message.id]);
            const isActionMode =
              activeActionMessageId?.toString() === message.id?.toString();
            const messageSizeClass = getMessageSizeClass(message.message);

            let dateComponent = null;
            if (formattedMessageDate !== previousDate) {
              dateComponent = (
                <div className="chatMessageItem2DateDemarcation">
                  {formattedMessageDate}
                </div>
              );
              previousDate = formattedMessageDate;
            }

            return (
              <div
                key={message.id}
                className={`chatMessageItem2 ${
                  message.id?.toString() ===
                  selectedRepliedMessageId?.toString()
                    ? "repliedMessage"
                    : ""
                }`}
                ref={
                  message.id?.toString() ===
                  selectedRepliedMessageId?.toString()
                    ? selectedRepliedMessageIdRef
                    : null
                }
              >
                {dateComponent}
                <div className={`chatMessageItem2Body ${messageOwner}`}>
                  <div className="chatMessageCompactLayout">
                    <div className="chatMessageAvatarColumn">
                      <Avatar
                        src={avatarSrc}
                        alt={usernameLabel}
                        className={`chatMessageAvatar ${userTeamCode}`}
                      >
                        {avatarFallback}
                      </Avatar>
                    </div>

                    <div className="chatMessageContentColumn">
                      <div className="chatMessageMetaRow">
                        <div className="chatMessageIdentityBlock">
                          <div
                            className={`chatMessageItem2UsernameBadge ${userTeamCode}`}
                          >
                            {usernameLabel}
                          </div>
                          <div className="chatMessageItem2Time">
                            {formattedMessageTime}
                          </div>
                        </div>

                        {isActionMode ? (
                          <div
                            className="chatMessageTapActions"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="chatMessageTapActionsCenter">
                              {!isMyMessage ? (
                                <>
                                  <IconButton
                                    className="chatExpandedActionButton"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleMsgInteraction(
                                        MESSAGE_INTERACTIONS_TYPES.LIKE,
                                        message.id
                                      );
                                    }}
                                    title="Like"
                                  >
                                    <ThumbUpIcon
                                      sx={{
                                        fontSize: "20px",
                                        color:
                                          myMsgInteractionType ===
                                          MESSAGE_INTERACTIONS_TYPES.LIKE
                                            ? "#ff6b35"
                                            : "#ffffff",
                                      }}
                                    />
                                  </IconButton>
                                  <IconButton
                                    className="chatExpandedActionButton"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleMsgInteraction(
                                        MESSAGE_INTERACTIONS_TYPES.DISLIKE,
                                        message.id
                                      );
                                    }}
                                    title="Dislike"
                                  >
                                    <ThumbDownIcon
                                      sx={{
                                        fontSize: "20px",
                                        color:
                                          myMsgInteractionType ===
                                          MESSAGE_INTERACTIONS_TYPES.DISLIKE
                                            ? "#ff4444"
                                            : "#ffffff",
                                      }}
                                    />
                                  </IconButton>
                                  <IconButton
                                    className="chatExpandedActionButton"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleMsgTipping(message.id);
                                    }}
                                    title="Tip"
                                  >
                                    <CardGiftcardIcon sx={{ fontSize: "20px", color: "#ffffff" }} />
                                  </IconButton>
                                  <IconButton
                                    className="chatExpandedActionButton"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleReplyMessage(message);
                                    }}
                                    title="Reply"
                                  >
                                    <ReplyIcon sx={{ fontSize: "20px", color: "#ffffff" }} />
                                  </IconButton>
                                </>
                              ) : (
                                <IconButton
                                  className="chatExpandedActionButton"
                                  aria-controls="chatMessageMenuOptions"
                                  aria-haspopup="true"
                                  aria-expanded={Boolean(anchorEl) ? "true" : undefined}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleMenuOptionClick(event, message);
                                  }}
                                  title="More options"
                                >
                                  <MenuIcon sx={{ fontSize: "20px", color: "#ffffff" }} />
                                </IconButton>
                              )}
                            </div>

                            <IconButton
                              className="chatExpandedCancelButton"
                              onClick={(event) => {
                                event.stopPropagation();
                                closeActiveMessageActions();
                              }}
                              title="Close actions"
                            >
                              <CancelIcon sx={{ fontSize: "22px", color: "#ffffff" }} />
                            </IconButton>
                          </div>
                        ) : (
                          <div
                            className="chatMessageTextContainer"
                            onClick={() => handleToggleMessageActions(message.id)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleToggleMessageActions(message.id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            title="Tap message to show actions"
                          >
                            <div
                              className={`chatMessageItem2Message ${messageSizeClass} ${
                                isExpandedMessage ? "expanded" : "collapsed"
                              }`}
                            >
                              {message.message}
                            </div>
                          </div>
                        )}
                      </div>

                      {message.reply_to_id &&
                        (getRepliedMessage ? (
                          <div
                            className="chatMessageItem2MessageReply"
                            onClick={() =>
                              handleScrollToRepliedMessage(getRepliedMessage.id)
                            }
                          >
                            Replied: <span>{truncatedRepliedMessage}</span>
                          </div>
                        ) : (
                          <div
                            className="chatMessageItem2MessageReply"
                            title="Refresh page to see hidden replied, if user is currently muted"
                          >
                            Hidden or Deleted Reply
                          </div>
                        ))}

                      {!isActionMode && isExpandableMessage && (
                        <button
                          type="button"
                          className="chatMessageReadMoreButton"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleExpandedMessage(message.id);
                          }}
                        >
                          {isExpandedMessage ? "Show less" : "Read more"}
                        </button>
                      )}

                      <Menu
                        id="chatMessageMenuOptions"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuOptionClose}
                        PaperProps={{
                          sx: {
                            backgroundColor:
                              "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                            background:
                              "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                            color: "#ffffff",
                            boxShadow:
                              "0px 8px 32px rgba(255, 107, 53, 0.3)",
                            border: "1px solid rgba(255, 107, 53, 0.3)",
                            borderRadius: "0px",
                            backdropFilter: "blur(10px)",
                          },
                        }}
                        transformOrigin={{
                          horizontal: "right",
                          vertical: "top",
                        }}
                        anchorOrigin={{
                          horizontal: "right",
                          vertical: "bottom",
                        }}
                      >
                        {isEditableMessage && (
                          <MenuItem
                            onClick={() => {
                              handleEditMessage();
                            }}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(255, 107, 53, 0.2)",
                              },
                              borderRadius: "0px",
                              margin: "4px",
                            }}
                            disableRipple
                          >
                            <EditIcon sx={{ mr: "8px", color: "#ff6b35" }} />
                            Edit
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => {
                            handleDeleteMessage();
                            handleMenuOptionClose();
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(255, 68, 68, 0.2)",
                            },
                            borderRadius: "0px",
                            margin: "4px",
                          }}
                          disableRipple
                        >
                          <DeleteIcon sx={{ mr: "8px", color: "#ff4444" }} />
                          Delete
                        </MenuItem>
                      </Menu>
                    </div>

                    <div className="chatMessageActionsColumn">
                      <div className="chatMessageCounterDisplay">
                        <div className="chatMessageCounterItem">
                          <ThumbUpIcon
                            sx={{
                              fontSize: "14px",
                              color:
                                myMsgInteractionType ===
                                MESSAGE_INTERACTIONS_TYPES.LIKE
                                  ? "#ff6b35"
                                  : "#ffffff",
                            }}
                          />
                          <span className="chatCompactActionCount">
                            {FormatNumberCounter(likeInteractionCount)}
                          </span>
                        </div>

                        <div className="chatMessageCounterItem">
                          <ThumbDownIcon
                            sx={{
                              fontSize: "14px",
                              color:
                                myMsgInteractionType ===
                                MESSAGE_INTERACTIONS_TYPES.DISLIKE
                                  ? "#ff4444"
                                  : "#ffffff",
                            }}
                          />
                          <span className="chatCompactActionCount">
                            {FormatNumberCounter(dislikeInteractionCount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {replyMessage && (
          <div className="chatReplyMessage">
            <span>{TruncateString(replyMessage.message, 300)}</span>
            <IconButton
              onClick={() => setReplyMessage(null)}
              title="Cancel Reply"
            >
              <CancelIcon
                sx={{
                  fontSize: "20px",
                  color: "#ffffff",
                  "&:hover": {
                    color: "#ff6b35",
                  },
                }}
              />
            </IconButton>
          </div>
        )}

        <div className={innerRoom ? "chatInput2" : "chatInput"}>
          {editedMessage ? (
            <textarea
              placeholder="Type a message..."
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus={editedMessage ? true : false}
            />
          ) : (
            <textarea
              placeholder="Type a message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus={!editedMessage ? true : false}
            />
          )}

          <div>
            <div className="chatTimerCountDown">
              {editedMessage
                ? null
                : countdown > 0
                ? `${countdown} secs`
                : null}
            </div>

            {editedMessage && (
              <div>
                <button
                  className="editMessageCancelButton"
                  onClick={cancelMessageEditing}
                >
                  Cancel
                </button>
              </div>
            )}

            <div>
              <button
                className="sendMessageButton"
                onClick={handleSendOrEdit}
                disabled={
                  editedMessage ? false : countdown > 0 || message === ""
                }
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessageField);
